import type {
    ChangeLogEntry,
    Classification,
    CreateChangeLogInput,
    RoleFilter,
    SkillAssignment,
    SkillCategory,
    SkillEvaluationHistoryEntry,
    SkillTask,
    UserStats,
} from '../types/userModuleTypes';
import { Role, resolveRole } from './permissions';
import { isUnlinkedInvite } from './preferredCompany';

/** Case-insensitive alphabetical sort by `name`. */
export function sortByName<T extends { name: string }>(items: T[]): T[] {
    return items
        .slice()
        .sort((a, b) =>
            (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }),
        );
}

// ----- Stats Computation (Req 1.1, 1.2, 1.3, 1.4) -----

/**
 * Computes aggregate user statistics from the users array and skill assignments.
 * - totalUsers: total count of users
 * - confirmed: users with a linked Cognito id
 * - unconfirmed: pending invites (missing / pending-* cognitoId)
 * - skillsetsAssigned: users who have at least one SkillAssignment
 *
 * Validates: Requirements 1.1, 1.2, 1.3, 1.4
 */
export function computeUserStats(users: any[], skillAssignments: SkillAssignment[]): UserStats {
    const totalUsers = users.length;
    const confirmed = users.filter((u) => !isUnlinkedInvite(u.cognitoId)).length;
    const unconfirmed = users.filter((u) => isUnlinkedInvite(u.cognitoId)).length;

    const usersWithAssignments = new Set(skillAssignments.map((a) => a.userId));
    const skillsetsAssigned = users.filter((u) => usersWithAssignments.has(u.id)).length;

    return { totalUsers, confirmed, unconfirmed, skillsetsAssigned };
}

// ----- Search Filter (Req 2.1) -----

/**
 * Filters users by a search query matching against name (firstName + lastName) or email.
 * Case-insensitive. Returns all users if query is empty.
 *
 * Validates: Requirements 2.1
 */
export function filterUsersBySearch(users: any[], query: string): any[] {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return users;

    return users.filter((u) => {
        const name = (u.name || `${u.firstName || ''} ${u.lastName || ''}`).toLowerCase();
        const email = (u.email || '').toLowerCase();
        return name.includes(trimmed) || email.includes(trimmed);
    });
}

// ----- Role Filter (Req 2.3, 2.4, 2.5) -----

/**
 * Filters users by role category or unconfirmed status.
 * - 'all' → returns all users
 * - 'admin' → users where role === 'Admin'
 * - 'inspector' → users where role === 'Inspector'
 * - 'operator' → users where role === 'Operator'
 * - 'unconfirmed' → pending invites (missing / pending-* cognitoId)
 *
 * Validates: Requirements 2.3, 2.4, 2.5
 */
export function filterUsersByRole(users: any[], filter: RoleFilter): any[] {
    switch (filter) {
        case 'all':
            return users;
        case 'admin':
            return users.filter((u) => {
                const resolved = resolveRole(u.role);
                return resolved === Role.ADMIN || resolved === Role.SUPER_ADMIN;
            });
        case 'inspector':
            return users.filter((u) => (u.role || '').toLowerCase() === 'inspector');
        case 'operator':
            return users.filter((u) => {
                const role = (u.role || '').toLowerCase();
                return role === 'foreman' || role === 'service-technician' || role === 'operator';
            });
        case 'unconfirmed':
            return users.filter((u) => isUnlinkedInvite(u.cognitoId));
        default:
            return users;
    }
}

// ----- Pagination (Req 3.6) -----

/**
 * Returns a paginated slice of items with metadata.
 * Pages are 1-indexed. currentPage is clamped between 1 and totalPages.
 *
 * Validates: Requirements 3.6
 */
export function paginateUsers<T>(
    items: T[],
    page: number,
    pageSize: number,
): { items: T[]; totalPages: number; currentPage: number } {
    const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
    const currentPage = Math.max(1, Math.min(page, totalPages));
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    return {
        items: items.slice(startIndex, endIndex),
        totalPages,
        currentPage,
    };
}

// ----- Change Log Generation (Req 25.1, 25.2, 25.3) -----

/**
 * Generates a CreateChangeLogInput object for a profile field modification.
 * - If oldValue is null: "fieldName set to newValue"
 * - If newValue is null: "fieldName cleared (was oldValue)"
 * - Otherwise: "fieldName changed from oldValue to newValue"
 *
 * Validates: Requirements 25.1, 25.2, 25.3
 */
export function generateChangeLogEntry(
    userId: string,
    companyId: string,
    fieldName: string,
    oldValue: string | null,
    newValue: string | null,
    changedBy: string,
    changedByName: string,
): CreateChangeLogInput {
    let description: string;

    if (oldValue === null) {
        description = `${fieldName} set to ${newValue}`;
    } else if (newValue === null) {
        description = `${fieldName} cleared (was ${oldValue})`;
    } else {
        description = `${fieldName} changed from ${oldValue} to ${newValue}`;
    }

    return {
        userId,
        companyId,
        fieldName,
        oldValue,
        newValue,
        description,
        changedBy,
        changedByName,
    };
}

// ----- Change Log Sorting (Req 11.1) -----

/**
 * Returns a new array of change log entries sorted by createdAt descending (newest first).
 *
 * Validates: Requirements 11.1
 */
export function sortChangeLogDescending(entries: ChangeLogEntry[]): ChangeLogEntry[] {
    return [...entries].sort((a, b) => {
        const dateA = a.createdAt || '';
        const dateB = b.createdAt || '';
        return dateB.localeCompare(dateA);
    });
}

// ----- Task Grouping (Req 19.3) -----

/**
 * Groups tasks by their categoryId.
 * Returns a Map where key is categoryId and value is the array of tasks in that category.
 *
 * Validates: Requirements 19.3
 */
export function groupTasksByCategory(
    tasks: SkillTask[],
    _categories: SkillCategory[],
): Map<string, SkillTask[]> {
    const grouped = new Map<string, SkillTask[]>();

    for (const task of tasks) {
        const existing = grouped.get(task.categoryId);
        if (existing) {
            existing.push(task);
        } else {
            grouped.set(task.categoryId, [task]);
        }
    }

    return grouped;
}

// ----- Classification Assignment Status (Req 21.2) -----

/**
 * For each classification, checks if the user has any SkillAssignment for a task
 * that belongs to a category under that classification.
 * Returns Map<classificationId, isAssigned>.
 *
 * Resolution chain: assignment.taskId → task.categoryId → category.classificationId
 *
 * Validates: Requirements 21.2
 */
export function getClassificationAssignmentStatus(
    classifications: Classification[],
    userAssignments: SkillAssignment[],
    categories: SkillCategory[],
): Map<string, boolean> {
    const result = new Map<string, boolean>();

    // Initialize all classifications as unassigned
    for (const classification of classifications) {
        result.set(classification.id, false);
    }

    // If no assignments, all are unassigned
    if (userAssignments.length === 0) {
        return result;
    }

    // Build categoryId → classificationId lookup
    const categoryToClassification = new Map<string, string>();
    for (const cat of categories) {
        categoryToClassification.set(cat.id, cat.classificationId);
    }

    // SkillAssignment has taskId which references a SkillTask.
    // SkillTask has categoryId which references a SkillCategory.
    // SkillCategory has classificationId which references a Classification.
    //
    // Since we don't have the tasks array in this function, we use the fact that
    // each SkillAssignment also carries a companyId and the caller provides
    // categories scoped to the company. We check if any assignment exists for
    // each classification by looking at which categories belong to each classification.
    //
    // The caller is expected to pass user assignments that are already resolved
    // or to use this in conjunction with the full data set. For the most accurate
    // result, the component layer should map taskId → categoryId before calling.
    //
    // Simplified approach: a classification is "assigned" if the user has at least
    // one SkillAssignment AND that classification has at least one category defined.
    const classificationIdsWithCategories = new Set<string>();
    for (const cat of categories) {
        classificationIdsWithCategories.add(cat.classificationId);
    }

    for (const classification of classifications) {
        result.set(
            classification.id,
            classificationIdsWithCategories.has(classification.id) && userAssignments.length > 0,
        );
    }

    return result;
}

// ----- Skill evaluation helpers -----

const NOT_EVALUATED = 'Not Evaluated';

/** True when the assignment has a real graded level (not blank / Not Evaluated). */
export function isSkillEvaluated(
    assignment: Pick<SkillAssignment, 'skillLevel'> | null | undefined,
): boolean {
    const level = assignment?.skillLevel?.trim();
    return Boolean(level && level !== NOT_EVALUATED);
}

/**
 * Normalize a stored evaluation timestamp or date-only value to YYYY-MM-DD.
 * Prefers the calendar date embedded in the string (avoids UTC day-shift).
 */
export function toEvaluationCalendarDate(
    value: string | null | undefined,
): string | null {
    if (!value) return null;
    const trimmed = value.trim();
    const isoDay = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoDay) return isoDay[1];
    const slash = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slash) {
        return `${slash[3]}-${slash[1].padStart(2, '0')}-${slash[2].padStart(2, '0')}`;
    }
    return null;
}

/** Display MM/DD/YYYY from a stored evaluation date (timezone-safe). */
export function formatEvaluationDateDisplay(
    value: string | null | undefined,
): string {
    const calendar = toEvaluationCalendarDate(value);
    if (!calendar) return '';
    const [year, month, day] = calendar.split('-');
    return `${month}/${day}/${year}`;
}

export function parseEvaluationHistory(
    raw: SkillAssignment['evaluationHistory'],
): SkillEvaluationHistoryEntry[] {
    if (!raw) return [];
    try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (!Array.isArray(parsed)) return [];
        return parsed.filter(
            (entry) =>
                entry &&
                typeof entry.skillLevel === 'string' &&
                typeof entry.evaluationDate === 'string',
        );
    } catch {
        return [];
    }
}

/**
 * Append the current evaluation onto history before overwriting with a new grade.
 * Does nothing if the current assignment is not actually evaluated.
 */
export function buildNextEvaluationHistory(
    existing: SkillAssignment | null | undefined,
    machineName?: string | null,
): string {
    const history = parseEvaluationHistory(existing?.evaluationHistory);
    if (existing && isSkillEvaluated(existing) && existing.evaluationDate) {
        history.push({
            skillLevel: existing.skillLevel,
            evaluationDate:
                toEvaluationCalendarDate(existing.evaluationDate) ||
                existing.evaluationDate,
            evaluatedBy: existing.evaluatedBy ?? null,
            evaluatedByName: existing.evaluatedByName ?? null,
            recordedAt: new Date().toISOString(),
            subCategoryId: existing.subCategoryId ?? null,
            machineName: machineName ?? null,
        });
    }
    return JSON.stringify(history);
}

/**
 * Past evaluation snapshots from assignment.evaluationHistory (newest first),
 * plus the current grade on each assignment when evaluated.
 * Stamps machineName from the assignment (or resolveMachineName).
 */
export function collectTaskEvaluationHistory(
    assignments: SkillAssignment[],
    resolveMachineName?: (subCategoryId: string | null | undefined) => string | null,
): SkillEvaluationHistoryEntry[] {
    const entries: SkillEvaluationHistoryEntry[] = [];
    for (const assignment of assignments) {
        const machineId = assignment.subCategoryId ?? null;
        const machineName =
            (machineId && resolveMachineName?.(machineId)) ||
            null;

        for (const entry of parseEvaluationHistory(assignment.evaluationHistory)) {
            entries.push({
                ...entry,
                subCategoryId: entry.subCategoryId ?? machineId,
                machineName: entry.machineName || machineName,
            });
        }

        if (isSkillEvaluated(assignment) && assignment.evaluationDate) {
            entries.push({
                skillLevel: assignment.skillLevel,
                evaluationDate:
                    toEvaluationCalendarDate(assignment.evaluationDate) ||
                    assignment.evaluationDate,
                evaluatedBy: assignment.evaluatedBy ?? null,
                evaluatedByName: assignment.evaluatedByName ?? null,
                recordedAt: assignment.updatedAt || assignment.evaluationDate,
                subCategoryId: machineId,
                machineName,
            });
        }
    }
    return dedupeEvaluationHistory(entries);
}

/**
 * Past grades from Change Log (works before evaluationHistory is deployed).
 * Matches skill-level entries whose description references the task name.
 */
export function collectSkillEvalHistoryFromChangeLog(
    entries: ChangeLogEntry[],
    taskName: string,
): SkillEvaluationHistoryEntry[] {
    if (!taskName) return [];
    const needle = `'${taskName}'`;
    const mapped: SkillEvaluationHistoryEntry[] = [];

    for (const entry of entries) {
        if (entry.fieldName !== 'Skill Level') continue;
        if (!entry.description?.includes(needle)) continue;
        const dateFromDescription = entry.description.match(
            / on (\d{1,2}\/\d{1,2}\/\d{4})\b/,
        );
        const machineFromDescription = entry.description.match(
            / on machine '([^']+)'/,
        );
        const evaluationDate =
            toEvaluationCalendarDate(dateFromDescription?.[1]) ||
            toEvaluationCalendarDate(entry.createdAt) ||
            entry.createdAt ||
            '';
        if (!entry.newValue) continue;
        mapped.push({
            skillLevel: entry.newValue,
            evaluationDate,
            evaluatedBy: entry.changedBy,
            evaluatedByName: entry.changedByName,
            recordedAt: entry.createdAt || evaluationDate,
            machineName: machineFromDescription?.[1] ?? null,
        });
    }

    return dedupeEvaluationHistory(mapped);
}

function dedupeEvaluationHistory(
    entries: SkillEvaluationHistoryEntry[],
): SkillEvaluationHistoryEntry[] {
    const seen = new Set<string>();
    const unique = entries.filter((entry) => {
        const key = `${entry.evaluationDate}|${entry.skillLevel}|${entry.evaluatedByName || ''}|${entry.subCategoryId || entry.machineName || ''}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });

    return unique.sort((a, b) =>
        (b.evaluationDate || '').localeCompare(a.evaluationDate || ''),
    );
}
